import React, { useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from 'date-fns';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export function CalendarView({ currentEvent, calendarMonth, setCalendarMonth }) {
  const calendarDays = useMemo(() => {
    const start = startOfMonth(calendarMonth);
    const end = endOfMonth(calendarMonth);
    return eachDayOfInterval({ start, end });
  }, [calendarMonth]);

  const getMatchesForDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return currentEvent?.schedule?.filter(m => m.date === dateStr) || [];
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setCalendarMonth(d => new Date(d.getFullYear(), d.getMonth() - 1))}
          data-testid="calendar-prev-month"
        >
          <ChevronLeft className="w-5 h-5" />
        </Button>
        <h2 className="text-xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
          {format(calendarMonth, 'MMMM yyyy').toUpperCase()}
        </h2>
        <Button
          variant="outline"
          size="icon"
          onClick={() => setCalendarMonth(d => new Date(d.getFullYear(), d.getMonth() + 1))}
          data-testid="calendar-next-month"
        >
          <ChevronRight className="w-5 h-5" />
        </Button>
      </div>

      <Card className="border-slate-200 shadow-card overflow-hidden">
        <div className="grid grid-cols-7 border-b border-slate-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="px-2 py-3 text-center text-sm font-semibold text-slate-500 bg-slate-50">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {Array.from({ length: calendarDays[0].getDay() }).map((_, i) => (
            <div key={`empty-${i}`} className="calendar-day bg-slate-50"></div>
          ))}
          {calendarDays.map(day => {
            const dayMatches = getMatchesForDay(day);
            const hasGames = dayMatches.length > 0;
            
            return (
              <div 
                key={day.toISOString()} 
                className={`calendar-day ${hasGames ? 'has-games' : ''}`}
              >
                <div className={`text-sm font-medium mb-2 ${
                  isSameDay(day, new Date()) ? 'text-[#CF142B]' : 'text-slate-700'
                }`}>
                  {format(day, 'd')}
                </div>
                <div className="space-y-1 overflow-y-auto max-h-24">
                  {dayMatches.slice(0, 3).map((match) => (
                    <div 
                      key={match.id}
                      className="calendar-event text-xs"
                      title={`${match.team_a_name} vs ${match.team_b_name} @ ${match.time}`}
                    >
                      {match.time} - {match.team_a_name?.substring(0, 8)} vs {match.team_b_name?.substring(0, 8)}
                    </div>
                  ))}
                  {dayMatches.length > 3 && (
                    <div className="text-xs text-slate-500 pl-1">
                      +{dayMatches.length - 3} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
